import { useState } from "react";
import { Link } from "react-router";
import { Aviso, Botao, Campo, Cartao, Chip } from "../ui";
import { mensagemDoConsole } from "../../nucleo/console";
import {
  CHIP_DA_FAIXA,
  FAIXAS,
  ORDENS,
  ROTULO_DA_FAIXA,
  ROTULO_DA_ORDEM,
  useCarteiraDeSaude,
  useDefinirResponsavelDeCS,
  useRecalcularSaude,
  useResponsaveisDeCS,
  variacaoLegivel,
  type Faixa,
  type LinhaDaCarteira,
  type OrdemDaCarteira,
  type PessoaDaEquipe,
} from "../../nucleo/sucesso";
import { Selecionar } from "./Selecionar";

/** A carteira de clientes ordenável por risco, com o responsável de CS de cada um. */
export function PainelDaCarteira({ podeGerenciar }: { podeGerenciar: boolean }) {
  const [faixa, setFaixa] = useState<Faixa | "">("");
  const [responsavel, setResponsavel] = useState("");
  const [busca, setBusca] = useState("");
  const [ordem, setOrdem] = useState<OrdemDaCarteira>("risco");
  const equipe = useResponsaveisDeCS();
  const carteira = useCarteiraDeSaude({
    faixa,
    responsavelId: responsavel === "sem" ? "" : responsavel,
    semResponsavel: responsavel === "sem",
    busca,
    ordem,
  });
  const recalcular = useRecalcularSaude();

  return (
    <div data-testid="painel-da-carteira">
      <Cartao titulo="Filtros" className="mb-4">
        <div className="grid gap-3 md:grid-cols-4">
          <Campo
            rotulo="Buscar cliente"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
          />
          <Selecionar rotulo="Faixa" value={faixa} onChange={(e) => setFaixa(e.target.value as Faixa | "")}>
            <option value="">Todas</option>
            {FAIXAS.map((f) => (
              <option key={f} value={f}>
                {ROTULO_DA_FAIXA[f]}
              </option>
            ))}
          </Selecionar>
          <Selecionar rotulo="Responsável de CS" value={responsavel} onChange={(e) => setResponsavel(e.target.value)}>
            <option value="">Todos</option>
            <option value="sem">Sem responsável</option>
            {(equipe.data ?? []).map((pessoa) => (
              <option key={pessoa.id} value={pessoa.id}>
                {pessoa.nome}
              </option>
            ))}
          </Selecionar>
          <Selecionar rotulo="Ordenar por" value={ordem} onChange={(e) => setOrdem(e.target.value as OrdemDaCarteira)}>
            {ORDENS.map((o) => (
              <option key={o} value={o}>
                {ROTULO_DA_ORDEM[o]}
              </option>
            ))}
          </Selecionar>
        </div>
        {podeGerenciar ? (
          <div className="mt-3">
            <Botao
              variante="fantasma"
              disabled={recalcular.isPending}
              onClick={() => recalcular.mutate(null)}
              data-testid="recalcular-tudo"
            >
              Recalcular a saúde de todos agora
            </Botao>
            {recalcular.isSuccess ? (
              <p role="status" className="mt-2" data-testid="recalculo-feito">
                Saúde recalculada para {recalcular.data.organizacoes}{" "}
                {recalcular.data.organizacoes === 1 ? "organização" : "organizações"}
                {recalcular.data.alertas > 0
                  ? `; ${recalcular.data.alertas} ${recalcular.data.alertas === 1 ? "queda de faixa avisada" : "quedas de faixa avisadas"}`
                  : ""}
                .
              </p>
            ) : null}
            {recalcular.isError ? <Aviso tom="erro" className="mt-2">{mensagemDoConsole(recalcular.error)}</Aviso> : null}
          </div>
        ) : null}
      </Cartao>

      {carteira.isPending ? <p role="status">Carregando a carteira…</p> : null}
      {carteira.isError ? <Aviso tom="erro">{mensagemDoConsole(carteira.error)}</Aviso> : null}
      {carteira.data && carteira.data.length === 0 ? (
        <Aviso tom="marca">Nenhum cliente com esses filtros.</Aviso>
      ) : null}
      {carteira.data && carteira.data.length > 0 ? (
        <div className="tabela-rolagem">
          <table className="tabela" data-testid="carteira-de-saude">
            <caption className="sr-only">Saúde dos clientes</caption>
            <thead>
              <tr>
                <th scope="col">Cliente</th>
                <th scope="col">Saúde</th>
                <th scope="col">Últimos 7 dias</th>
                <th scope="col">Responsável de CS</th>
              </tr>
            </thead>
            <tbody>
              {carteira.data.map((linha) => (
                <LinhaDaCarteiraDeSaude
                  key={linha.organizacao.id}
                  linha={linha}
                  equipe={equipe.data ?? []}
                  podeGerenciar={podeGerenciar}
                />
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </div>
  );
}

function LinhaDaCarteiraDeSaude({
  linha,
  equipe,
  podeGerenciar,
}: {
  linha: LinhaDaCarteira;
  equipe: PessoaDaEquipe[];
  podeGerenciar: boolean;
}) {
  const definir = useDefinirResponsavelDeCS(linha.organizacao.id);
  return (
    <tr data-testid={`saude-${linha.organizacao.nome}`} data-faixa={linha.faixa ?? "sem-retrato"}>
      <td>
        <Link to={`/admin/sucesso/${linha.organizacao.id}`}>{linha.organizacao.nome}</Link>
      </td>
      <td>
        {linha.faixa && linha.score !== null ? (
          <Chip estado={CHIP_DA_FAIXA[linha.faixa]}>
            {ROTULO_DA_FAIXA[linha.faixa]} · {linha.score}
          </Chip>
        ) : (
          <span className="celula-secundaria">Ainda não calculada</span>
        )}
      </td>
      <td>{variacaoLegivel(linha.variacao_7_dias)}</td>
      <td>
        {podeGerenciar ? (
          <>
            <select
              className="campo__controle"
              aria-label={`Responsável de CS de ${linha.organizacao.nome}`}
              value={linha.responsavel?.id ?? ""}
              disabled={definir.isPending}
              onChange={(e) => definir.mutate(e.target.value || null)}
            >
              <option value="">Sem responsável</option>
              {(equipe.length > 0 ? equipe : linha.responsavel ? [linha.responsavel] : []).map((pessoa) => (
                <option key={pessoa.id} value={pessoa.id}>
                  {pessoa.nome}
                </option>
              ))}
            </select>
            {definir.isError ? <p role="alert">{mensagemDoConsole(definir.error)}</p> : null}
          </>
        ) : (
          (linha.responsavel?.nome ?? <span className="celula-secundaria">Sem responsável</span>)
        )}
      </td>
    </tr>
  );
}
